'use client';

import ArticlePreviews from './ArticlePreviews';
import { useState, useEffect } from 'react';

export default function TabbedCardList({ block, position }) {
  const uid = block?.tabbed_card_list?._metadata?.uid ?? `tabbed_card_list`;
  const { articles } = block?.tabbed_card_list || {};
  const {
    categories,
    articlesGroupedByCategory,
    latestArticles,
    priorityArticles
  } = articles || {};
  const [selectedCategory, setSelectedCategory] = useState('');

  // Set initial category based on the first category slug or URL hash
  useEffect(() => {
    const initialCategorySlug = window.location.hash
      ? window.location.hash.replace('#', '')
      : categories[0]?.category_slug;
    setSelectedCategory(initialCategorySlug);
  }, [categories]);

  // Handle category change
  function toggleCategory(e) {
    const newCategory = e.target.id.replace('tab-', '');
    if (newCategory !== selectedCategory) {
      setSelectedCategory(newCategory);
      window.history.pushState(
        null,
        '',
        `/pages/resource-guide#${newCategory}`
      );
    }
  }

  // Handle keyboard navigation for tabs
  function handleKeyDown(e) {
    const currentIndex = categories.findIndex(
      (category) => category.category_slug === selectedCategory
    );
    let newIndex = currentIndex;

    switch (e.key) {
      case 'ArrowLeft':
      case 'ArrowUp':
        e.preventDefault();
        newIndex = currentIndex > 0 ? currentIndex - 1 : categories.length - 1;
        break;
      case 'ArrowRight':
      case 'ArrowDown':
        e.preventDefault();
        newIndex = currentIndex < categories.length - 1 ? currentIndex + 1 : 0;
        break;
      case 'Home':
        e.preventDefault();
        newIndex = 0;
        break;
      case 'End':
        e.preventDefault();
        newIndex = categories.length - 1;
        break;
      default:
        return;
    }

    const newCategory = categories[newIndex].category_slug;
    setSelectedCategory(newCategory);
    window.history.pushState(null, '', `/pages/resource-guide#${newCategory}`);

    // Focus the new tab
    const newTab = document.getElementById(`tab-${newCategory}`);
    if (newTab) {
      newTab.focus();
    }
  }

  // Make sure back buttons and such work with the hash
  useEffect(() => {
    const handleHashChanged = () => {
      const newCategory = window.location.hash.replace('#', '');
      if (newCategory === selectedCategory) {
        return;
      }

      // If a new category is specified in the hash, set it if applicable
      if (newCategory) {
        const categoryExists = categories?.some(
          (category) => category.category_slug === newCategory
        );
        if (categoryExists) {
          setSelectedCategory(newCategory);
          return;
        }

        // Return because this hash is not meant for categories, maybe it's an ID
        return;
      }

      // If there is no hash at all, default to the first category
      const firstCategory = categories?.[0]?.category_slug;
      if (firstCategory) {
        setSelectedCategory(firstCategory);
      }
    };

    window.addEventListener('hashchange', handleHashChanged);
    return () => {
      window.removeEventListener('hashchange', handleHashChanged);
    };
  }, [selectedCategory, categories]);

  return (
    <section id={`${uid}_${position}`} data-name="tabbed-card-list">
      <div
        className="mx-auto flex max-w-[996px] flex-wrap justify-center px-4 sm:flex-nowrap sm:px-11"
        role="tablist"
        aria-label="Resource guide categories"
      >
        {categories?.map((blog, index) => (
          <div
            key={index}
            className={`hover:border-parlor-green max-w-[208px] text-center hover:border-b-2 sm:max-w-full lg:w-full ${
              selectedCategory === blog.category_slug
                ? 'active border-parlor-green border-b-2'
                : 'border-dawn-grey-60 border-b'
            }`}
          >
            <button
              id={`tab-${blog.category_slug}`}
              className="blog-filter-s lg:blog-filter-r mx-auto mt-4 w-[108px] cursor-pointer border-none bg-transparent pt-4 pb-6 text-center whitespace-nowrap text-black sm:w-[136px] sm:pt-0"
              onClick={(e) => toggleCategory(e)}
              onKeyDown={handleKeyDown}
              role="tab"
              aria-selected={selectedCategory === blog.category_slug}
              aria-controls={`tabpanel-${blog.category_slug}`}
              tabIndex={selectedCategory === blog.category_slug ? 0 : -1}
              type="button"
            >
              {blog.label}
            </button>
          </div>
        ))}
      </div>

      <div
        id={`tabpanel-${selectedCategory}`}
        role="tabpanel"
        aria-labelledby={`tab-${selectedCategory}`}
        tabIndex={0}
      >
        <ArticlePreviews
          categories={categories}
          selectedCategory={selectedCategory}
          latestArticles={latestArticles}
          articlesGroupedByCategory={articlesGroupedByCategory}
          priorityArticles={priorityArticles}
        />
      </div>
    </section>
  );
}
